import heroIllustration from "../assets/illustrations/guide/common-questions/hero-lantern-mind.png";
import overthinkingIllustration from "../assets/illustrations/guide/common-questions/overthinking-mental-noise.png";
import replayIllustration from "../assets/illustrations/guide/common-questions/replay-conversations.png";
import thinkClearlyIllustration from "../assets/illustrations/guide/common-questions/think-clearly.png";
import organizeIllustration from "../assets/illustrations/guide/common-questions/organize-thoughts-insight.png";
import emotionsIllustration from "../assets/illustrations/guide/common-questions/emotions-decisions.png";
import exhaustionIllustration from "../assets/illustrations/guide/common-questions/mental-exhaustion-open-loop.png";
import overanalyzeIllustration from "../assets/illustrations/guide/common-questions/overanalyze.png";
import betterDecisionsIllustration from "../assets/illustrations/guide/common-questions/better-decisions.png";
import { COMMON_QUESTION_ARTICLES } from "./commonQuestionArticles.js";

export const COMMON_QUESTIONS_PAGE = {
  path: "/guide/common-questions",
  url: "https://ahavibes.xyz/guide/common-questions",
  title: "Common Questions We Know but Never Ask",
  pageTitle: "Common Questions We Know but Never Ask | Ahavibes",
  subhead: "The things your mind already suspects — just never says out loud.",
  description:
    "Answers to quiet questions about overthinking, mental clarity, and better decisions — with a clear path from looping thoughts to a deliberate response.",
  lead:
    "You've asked yourself some version of these questions before — usually at 2am, or in the quiet after a hard day. They're not signs that something's wrong with you; they're the quiet cost of a mind that never stops thinking. Let's finally sit with them.",
  heroIllustration,
  heroIllustrationAlt:
    "Person holding a lantern at the entrance of a winding road inside a head silhouette — finding a clear path through mental noise.",
};

/** SEO-safe lowercase kebab slug from a question title (no apostrophes/spaces). */
export function slugifyQuestion(question) {
  return String(question || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const COMMON_QUESTIONS = [
  {
    id: "why-cant-i-stop-overthinking",
    question: "Why can't I stop overthinking?",
    eyebrow: "For minds too sharp to switch off",
    pageTitle: "A Smart Mind, Stuck on Repeat | Ahavibes",
    answerParagraphs: [
      "You can't stop overthinking because your mind is trying to resolve an unclear interpretation, not the actual event. Overthinking isn't extra thinking — it's the same unresolved thought looping because it was never clearly separated from the meaning you attached to it.",
      "Your mind treats an unfinished thought like an open tab — it keeps pulling attention back until it feels resolved. Most overthinking isn't solving anything; it's replaying the same interpretation without new information. In the EIER framework (Event → Interpretation → Evidence → Response), overthinking almost always means you're stuck between Interpretation and Evidence, never reaching a Response.",
    ],
    ctaLabel: "Process your looping thought",
    guidePath: "/mental-noise/mental-noise-vs-overthinking",
    guideLabel: "Mental Noise vs Overthinking",
    illustration: overthinkingIllustration,
    illustrationAlt:
      "Person surrounded by shadowy figures representing looping, unresolved thoughts.",
    keywords: ["overthinking", "mental noise", "loop", "interpretation"],
  },
  {
    id: "why-do-i-replay-conversations",
    question: "Why do I replay conversations?",
    eyebrow:
      "High performers rehearse. It's not weakness — it's an unfinished loop.",
    pageTitle: "Still Replaying It? Here's Why | Ahavibes",
    answerParagraphs: [
      "You replay conversations because your mind is searching for a version where you said or did something differently — this is rumination, not reflection, and it keeps you stuck rather than resolved. Reflection asks \"what can I learn?\" Rumination just asks \"what if?\" on repeat.",
      "The replay loop feels productive because it feels like problem-solving. But rumination rarely updates the Interpretation — it just deepens the same groove. Real resolution comes from naming what actually happened (the Event), separating it from what you assumed it meant (the Interpretation), checking Evidence, and then letting the loop close.",
    ],
    ctaLabel: "Break the replay loop",
    guidePath: "/structured-thinking/reflection-vs-rumination",
    guideLabel: "Reflection vs Rumination",
    illustration: replayIllustration,
    illustrationAlt:
      "Person replaying conversation fragments in looping thought bubbles.",
    keywords: ["replay", "rumination", "conversations", "reflection"],
  },
  {
    id: "why-cant-i-think-clearly",
    question: "Why can't I think clearly?",
    eyebrow: "Even the sharpest minds get foggy under load",
    pageTitle: "The Static Behind a Busy Mind | Ahavibes",
    answerParagraphs: [
      "You can't think clearly when too many unprocessed thoughts, reactions, and decisions are competing for the same mental space at once. Mental clarity isn't about having a calm life — it's about reducing how much your mind is holding unprocessed at any given moment.",
      "Clarity is a byproduct of structure, not a personality trait. When an event, your interpretation of it, and the pressure you feel about it are all tangled together, your mind experiences that as noise — even if nothing \"big\" is actually wrong. Separating those threads is what mental clarity looks like in practice.",
    ],
    ctaLabel: "Clear the noise",
    guidePath: "/mental-clarity/what-is-mental-clarity",
    guideLabel: "What Is Mental Clarity",
    illustration: thinkClearlyIllustration,
    illustrationAlt:
      "Person standing as tangled thought threads separate into clear ordered strands.",
    keywords: ["clarity", "mental clarity", "noise", "structure"],
  },
  {
    id: "how-do-i-organize-my-thoughts",
    question: "How do I organize my thoughts?",
    eyebrow: "You don't need fewer thoughts. You need structure.",
    pageTitle: "Turn Mental Chaos Into Order | Ahavibes",
    answerParagraphs: [
      "The fastest way to organize your thoughts is to get them out of your head and into a structure — such as Event, Interpretation, Evidence, Response (EIER) — so each thought has a defined place instead of circling indefinitely.",
      "Thoughts feel chaotic when they're unstructured, not because there are too many of them. Writing a thought down and labeling it — is this what happened, what I think it means, what evidence I have, or what I plan to do — turns a tangled mental knot into four manageable pieces.",
    ],
    ctaLabel: "Organize your thoughts",
    guidePath: "/structured-thinking/thinking-frameworks",
    guideLabel: "Thinking Frameworks",
    illustration: organizeIllustration,
    illustrationAlt:
      "Person with a book and lightbulb moment after working through a tangle of ideas.",
    keywords: ["organize", "structure", "EIER", "framework"],
  },
  {
    id: "how-do-emotions-affect-decisions",
    question: "How do emotions affect decisions?",
    eyebrow: "The decision you made wasn't just logic — and that's not a flaw",
    pageTitle: "What's Really Driving Your Decisions | Ahavibes",
    answerParagraphs: [
      "Emotions affect decisions by influencing which interpretation of a situation feels true, often before you're consciously aware a decision is being made. An unnamed reaction doesn't disappear — it just steers the decision from the background instead of being factored in on purpose.",
      "This is why the same situation can produce a generous decision on a calm day and a defensive one on a stressful day — the facts didn't change, but the filter did. Naming what you're feeling specifically, then checking Evidence for the Interpretation behind it, is what allows a decision to be a genuine Response instead of a disguised reaction.",
    ],
    ctaLabel: "See your decision clearly",
    guidePath: "/emotional-awareness/emotional-triggers",
    guideLabel: "Emotional Triggers",
    illustration: emotionsIllustration,
    illustrationAlt:
      "Person at a crossroads with warm and cool filters showing how feelings shape interpretation.",
    keywords: ["emotions", "decisions", "triggers", "response"],
  },
  {
    id: "why-am-i-mentally-exhausted",
    question: "Why am I mentally exhausted?",
    eyebrow: "Tired without doing much? That's a high-performer tell.",
    pageTitle: "The Exhaustion No One Sees | Ahavibes",
    answerParagraphs: [
      "Mental exhaustion usually comes from unresolved thinking, not just a busy schedule — every unprocessed worry, decision, or replayed conversation keeps quietly consuming mental energy in the background, even when you're not actively thinking about it.",
      "This is often called the \"open loop\" effect: your mind treats anything unresolved as still active, whether it's a decision you haven't made or a conversation you haven't finished examining. The exhaustion isn't a sign you need to do less — it's a sign you're carrying too much unprocessed.",
    ],
    ctaLabel: "Offload what's weighing on you",
    guidePath: "/mental-clarity/stress-reduces-clarity",
    guideLabel: "How Stress Reduces Mental Clarity",
    illustration: exhaustionIllustration,
    illustrationAlt:
      "Person at a quiet desk late at night with a lingering thought and a spark of clarity.",
    keywords: ["exhausted", "open loop", "stress", "mental load"],
  },
  {
    id: "why-do-i-overanalyze-everything",
    question: "Why do I overanalyze everything?",
    eyebrow: "Precision is a strength — until it stalls you",
    pageTitle: "When Thinking It Through Becomes the Problem | Ahavibes",
    answerParagraphs: [
      "You overanalyze because your mind is trying to reach certainty before deciding, but certainty rarely comes before a decision — only after. Overanalyzing is often a search for a guarantee that doesn't exist, not a genuine effort to find more facts.",
      "The tell is this: if more information would actually change your analysis, that's due diligence. If you're circling the same facts hoping to feel different about them, that's overanalysis. The fix isn't gathering more data — it's separating fact from assumption and accepting \"clear enough\" over \"certain.\"",
    ],
    ctaLabel: "Stop the analysis loop",
    guidePath: "/structured-thinking/evidence-based-thinking",
    guideLabel: "Evidence-Based Thinking",
    illustration: overanalyzeIllustration,
    illustrationAlt:
      "Person circling the same evidence with many magnifying glasses, seeking certainty.",
    keywords: ["overanalyze", "certainty", "evidence", "facts"],
  },
  {
    id: "how-do-i-make-better-decisions",
    question: "How do I make better decisions?",
    eyebrow: "Built for people who refuse to decide on autopilot",
    pageTitle: "Decide Like You Mean It | Ahavibes",
    answerParagraphs: [
      "You make better decisions by separating what actually happened from what you're assuming it means, and from how you feel about it — then deciding deliberately instead of reacting. This is the Event → Interpretation → Evidence → Response (EIER) process.",
      "Most poor decisions aren't the result of bad judgment — they're the result of deciding from an unexamined interpretation or an unnamed reaction. Once those are separated from the facts and checked against Evidence, the right response is often much more obvious than it felt a few minutes earlier.",
    ],
    ctaLabel: "Work through your decision",
    guidePath: "/better-decisions/clear-decision-making",
    guideLabel: "Clear Decision Making",
    illustration: betterDecisionsIllustration,
    illustrationAlt:
      "Person taking a deliberate step forward along a clear four-step path.",
    keywords: ["decisions", "EIER", "response", "interpretation"],
  },
  ...COMMON_QUESTION_ARTICLES,
];

export function commonQuestionPath(questionId) {
  return `${COMMON_QUESTIONS_PAGE.path}/${questionId}`;
}

export function commonQuestionUrl(questionId) {
  return `${COMMON_QUESTIONS_PAGE.url}/${questionId}`;
}

export function getCommonQuestionById(questionId) {
  return COMMON_QUESTIONS.find((q) => q.id === questionId) || null;
}

export function getCommonQuestionNeighbors(questionId) {
  const index = COMMON_QUESTIONS.findIndex((q) => q.id === questionId);
  if (index < 0) return { prev: null, next: null };
  return {
    prev: index > 0 ? COMMON_QUESTIONS[index - 1] : null,
    next: index < COMMON_QUESTIONS.length - 1 ? COMMON_QUESTIONS[index + 1] : null,
  };
}

/** Normalize related guide links (legacy guidePath/guideLabel or relatedGuides[]). */
export function getRelatedGuides(question) {
  if (!question) return [];
  if (Array.isArray(question.relatedGuides) && question.relatedGuides.length) {
    return question.relatedGuides;
  }
  if (question.guidePath) {
    return [{ path: question.guidePath, label: question.guideLabel || "Related guide" }];
  }
  return [];
}

export function getQuestionMetaDescription(question) {
  if (!question) return "";
  return question.metaDescription || question.answerParagraphs?.[0] || "";
}

/** Index page: breadcrumbs + ItemList of all question URLs (for crawl/search). */
export function buildCommonQuestionsIndexJsonLd(page, questions) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://ahavibes.xyz/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Guide",
            item: "https://ahavibes.xyz/guide",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: page.title,
            item: page.url,
          },
        ],
      },
      {
        "@type": "CollectionPage",
        "@id": page.url,
        name: page.title,
        description: page.description,
        url: page.url,
        isAccessibleForFree: true,
        inLanguage: "en",
        mainEntity: {
          "@type": "ItemList",
          name: page.title,
          numberOfItems: questions.length,
          itemListElement: questions.map((q, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: q.question,
            url: commonQuestionUrl(q.id),
            description: q.answerParagraphs[0],
          })),
        },
      },
    ],
  };
}

/** Detail page: breadcrumbs + Article + FAQPage (crawl / AI / rich-result friendly). */
export function buildCommonQuestionPageJsonLd(page, question) {
  const url = commonQuestionUrl(question.id);
  const answerParts = [...(question.answerParagraphs || [])];
  if (question.askYourself?.length) {
    answerParts.push(`Ask yourself: ${question.askYourself.join(" ")}`);
  }
  if (question.tryAhavibes) {
    answerParts.push(`Try Ahavibes: ${question.tryAhavibes}`);
  }
  const answerText = answerParts.join(" ");
  const description = getQuestionMetaDescription(question);
  const related = getRelatedGuides(question);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://ahavibes.xyz/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Guide",
            item: "https://ahavibes.xyz/guide",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: page.title,
            item: page.url,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: question.question,
            item: url,
          },
        ],
      },
      {
        "@type": "Article",
        "@id": `${url}#article`,
        headline: question.question,
        name: question.question,
        description,
        keywords: (question.keywords || []).join(", "),
        url,
        mainEntityOfPage: url,
        isAccessibleForFree: true,
        inLanguage: "en",
        isPartOf: {
          "@type": "CollectionPage",
          "@id": page.url,
          name: page.title,
          url: page.url,
        },
        about: (question.keywords || []).map((name) => ({
          "@type": "Thing",
          name,
        })),
        author: {
          "@type": "Organization",
          name: "Ahavibes",
          url: "https://ahavibes.xyz",
        },
        publisher: {
          "@type": "Organization",
          name: "Ahavibes",
          url: "https://ahavibes.xyz",
          logo: {
            "@type": "ImageObject",
            url: "https://ahavibes.xyz/logo.png?v=20260912e",
          },
        },
        mentions: related.map((g) => ({
          "@type": "WebPage",
          name: g.label,
          url: g.path.startsWith("http") ? g.path : `https://ahavibes.xyz${g.path}`,
        })),
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        url,
        isAccessibleForFree: true,
        inLanguage: "en",
        isPartOf: { "@id": page.url },
        mainEntity: [
          {
            "@type": "Question",
            "@id": `${url}#question`,
            name: question.question,
            url,
            acceptedAnswer: {
              "@type": "Answer",
              text: answerText,
            },
          },
        ],
      },
    ],
  };
}
